import { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { formatCompact, formatShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { BookmarkPlus, Trash2, GitCompareArrows, Share2 } from "lucide-react";

const SCENARIO_COLORS = ["#2D6A4F", "#D96B43", "#D99B26"];

const ROWS = [
  { key: "strategyLabel", label: "Strategy" },
  { key: "retireAge", label: "Retire Age" },
  { key: "retirementCorpus", label: "Corpus at Retirement", money: true },
  { key: "firstYearExpense", label: "1st Yr Expense", money: true },
  { key: "swr", label: "Safe Withdrawal Rate", pct: true },
  { key: "lasts", label: "Money Lasts Until" },
  { key: "finalCorpus", label: "Final Corpus", money: true },
];

export const ScenarioCompare = ({ scenarios, currency, onSave, onRemove, onClear, onShare, canSave }) => {
  const chartData = useMemo(() => {
    if (!scenarios.length) return [];
    const byAge = {};
    scenarios.forEach((sc) => {
      sc.result.projection.forEach((p) => {
        byAge[p.age] = byAge[p.age] || { age: p.age };
        byAge[p.age][sc.label] = Math.max(0, Math.round(p.close));
      });
    });
    return Object.values(byAge).sort((a, b) => a.age - b.age);
  }, [scenarios]);

  const cell = (sc, row) => {
    const r = sc.result;
    const i = sc.inputs;
    if (row.key === "strategyLabel")
      return i.strategy === "swp" ? "Simple SWP" : `${i.buckets.length}-Bucket`;
    if (row.key === "retireAge") return i.retirementAge;
    if (row.key === "lasts")
      return r.depletionAge !== null ? `Age ${r.depletionAge}` : `Full (${i.longevityAge})`;
    if (row.money) return formatCompact(r[row.key], sc.inputs.currency);
    if (row.pct) return `${r[row.key].toFixed(2)}%`;
    return r[row.key];
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6" data-testid="scenario-compare">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-start gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#D96B43]/12 flex items-center justify-center shrink-0">
            <GitCompareArrows className="h-4 w-4 text-[#D96B43]" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#1A231F] leading-tight">Compare Scenarios</h3>
            <p className="text-sm text-[#8A9790]">Save up to 3 plans and see the trade-offs side by side.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="save-scenario-btn" onClick={onSave} disabled={!canSave}
            className="bg-[#1E2B25] hover:bg-[#2b3d34] text-white rounded-full px-4 h-9 text-sm">
            <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save Current
          </Button>
          {scenarios.length > 0 && (
            <Button data-testid="clear-scenarios-btn" onClick={onClear} variant="outline"
              className="rounded-full px-3 h-9 text-sm border-[#E8E3D9] text-[#5C6A64]">
              Clear
            </Button>
          )}
        </div>
      </div>

      {scenarios.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-[#E8E3D9] bg-[#FBF9F5] p-6 text-center text-sm text-[#8A9790]"
          data-testid="scenario-empty">
          Adjust your plan, then tap <span className="font-semibold text-[#1E2B25]">Save Current</span> to bank it.
          Tweak the inputs and save a second version to compare.
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="scenario-table">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-semibold text-[#8A9790]">Metric</th>
                  {scenarios.map((sc, idx) => (
                    <th key={sc.id} className="text-right py-2 px-3">
                      <AnimatePresence>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-end gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SCENARIO_COLORS[idx] }} />
                          <span className="font-heading font-bold text-[#1A231F]">{sc.label}</span>
                          <button data-testid={`share-scenario-${idx}`} onClick={() => onShare(sc)}
                            className="text-[#8A9790] hover:text-[#2D6A4F]" title="Copy share link">
                            <Share2 className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`remove-scenario-${idx}`} onClick={() => onRemove(sc.id)}
                            className="text-[#8A9790] hover:text-[#991B1B]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </motion.div>
                      </AnimatePresence>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.key} className="border-t border-[#E8E3D9]">
                    <td className="py-2 pr-3 text-[#5C6A64]">{row.label}</td>
                    {scenarios.map((sc) => (
                      <td key={sc.id} className="py-2 px-3 text-right font-mono font-semibold text-[#1A231F]">
                        {cell(sc, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3D9" vertical={false} />
                <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#8A9790" }} tickLine={false} axisLine={{ stroke: "#E8E3D9" }} />
                <YAxis tickFormatter={(v) => formatShort(v, currency)} tick={{ fontSize: 11, fill: "#8A9790" }} tickLine={false} axisLine={false} width={52} />
                <Tooltip
                  formatter={(v) => formatCompact(v, currency)}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E8E3D9", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {scenarios.map((sc, idx) => (
                  <Line key={sc.id} type="monotone" dataKey={sc.label} stroke={SCENARIO_COLORS[idx]}
                    strokeWidth={2.5} dot={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};
